// Copyright (c) 2024-2026 AlphaGrade. All Rights Reserved.
// Proprietary and confidential. See LICENSE file in project root.
import React, { useState } from 'react';
import './ShareGrade.css';
import { getChartView } from '../../utils/chartView';

/**
 * ShareGrade — the single share control for a ticker.
 *
 * 2026-09-12: this became a picker. There were FOUR share affordances on one
 * card - two "🔗 Share" buttons, this one, and a "Share chart" button tucked
 * inside the chart's own header. Three did the same thing in different places
 * and the one that did something different was the one nobody could find. The
 * others are gone; pick what you are sharing here instead.
 *
 * "The whole grade" posts /grade/{TICKER}, which unfurls into the score card.
 * "This chart" posts /chart/{TICKER} at whatever metric and timeframe is on
 * screen (see utils/chartView.js), which unfurls into the price-over-
 * fundamentals image.
 *
 * Pre-warm: before X scrapes the URL, we ping the public grade page + image so
 * the server-side cache is hot and X's first scrape is instant — which makes the
 * card far more likely to render on the first post (X scrapes asynchronously and
 * otherwise often shows a bare link on first encounter).
 */
const SITE_ORIGIN =
  (typeof window !== 'undefined' && window.location && window.location.origin) ||
  'https://alphagrade.io';

export default function ShareGrade({ symbol, conviction, label }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const sym = (symbol || '').toUpperCase().trim();
  if (!sym) return null;

  const url = `${SITE_ORIGIN}/grade/${sym}`;

  // Warm the public grade cache (grade JSON + OG image) so the social scrape is
  // a cache hit. Fire-and-forget; never block the share action.
  const prewarm = () => {
    try {
      fetch(`/api/public/grade/${sym}`).catch(() => {});
      fetch(`/grade/${sym}/og.png`, { mode: 'no-cors' }).catch(() => {});
    } catch {
      /* best-effort */
    }
  };

  const buildText = () => {
    if (conviction != null && label) {
      return `$${sym}: AlphaGrade conviction ${Math.round(conviction)}/100 (${label}) 👇`;
    }
    return `My AlphaGrade read on $${sym} 👇`;
  };

  // Each share gets a UNIQUE token so X can't serve a previously-cached card for
  // this ticker. X caches cards by URL for ~7 days and deprecated its re-scrape
  // tool, so a fresh token per share is the reliable way to always unfurl a
  // current card. The server ignores the query param (renders the live grade).
  const makeShareUrl = () =>
    `${url}?v=${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

  const shareOnX = () => {
    prewarm();
    const shareUrl = makeShareUrl();
    const intent =
      `https://x.com/intent/tweet?text=${encodeURIComponent(buildText())}` +
      `&url=${encodeURIComponent(shareUrl)}`;
    window.open(intent, '_blank', 'noopener,noreferrer');
  };

  const copyLink = async () => {
    prewarm();
    const shareUrl = makeShareUrl();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Copy this link:', shareUrl);
    }
  };

  const chartUrl = () => {
    const v = getChartView(sym);
    return `${SITE_ORIGIN}/chart/${sym}?metric=${v.metric}&range=${v.range}`;
  };

  const shareChartOnX = () => {
    const v = getChartView(sym);
    // Warm the image so X's first scrape is a cache hit, same as prewarm() does
    // for the grade card.
    try { fetch(`/chart/${sym}.png?metric=${v.metric}&range=${v.range}`, { mode: 'no-cors' }).catch(() => {}); } catch { /* best effort */ }
    const fresh = `${chartUrl()}&v=${Date.now().toString(36)}`;
    const text = `$${sym} price vs ${v.metric === 'fcf' ? 'free cash flow' : v.metric === 'eps' ? 'EPS' : 'revenue'} 👇`;
    window.open(
      `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(fresh)}`,
      '_blank', 'noopener,noreferrer',
    );
    setOpen(false);
  };

  const copyChartLink = async () => {
    const link = `${chartUrl()}&v=${Date.now().toString(36)}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Copy this link:', link);
    }
    setOpen(false);
  };

  if (!open) {
    return (
      <div className="share-grade">
        <button className="share-grade-btn share-grade-x" onClick={() => setOpen(true)} title={`Share ${sym}`}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
          Share
        </button>
      </div>
    );
  }

  return (
    <div className="share-grade share-grade-open">
      <div className="share-grade-head">
        <span className="share-grade-label">What do you want to share?</span>
        <button className="share-grade-close" onClick={() => setOpen(false)} aria-label="Close share options">×</button>
      </div>

      <div className="share-grade-opt">
        <div className="share-grade-opt-text">
          <strong>The whole grade</strong>
          <span>{sym} conviction score and the reasoning behind it</span>
        </div>
        <div className="share-grade-opt-btns">
          <button className="share-grade-btn share-grade-x" onClick={() => { shareOnX(); setOpen(false); }}>Post on X</button>
          <button className="share-grade-btn" onClick={copyLink}>{copied ? 'Copied ✓' : 'Copy link'}</button>
        </div>
      </div>

      <div className="share-grade-opt">
        <div className="share-grade-opt-text">
          <strong>This chart</strong>
          <span>Price over fundamentals, at the view you have open</span>
        </div>
        <div className="share-grade-opt-btns">
          <button className="share-grade-btn share-grade-x" onClick={shareChartOnX}>Post on X</button>
          <button className="share-grade-btn" onClick={copyChartLink}>Copy link</button>
        </div>
      </div>
    </div>
  );
}
